import { createContext, use, useCallback, useState, type ReactNode } from 'react';

type ApprovedResourcesContextValue = {
  /**
   * Whether the user has opted to allow all non-DELETE changes.
   * Set to `true` by either enabling the "Apply changes automatically" toggle
   * or clicking "Allow all changes" in the approval dropdown — they share this state.
   */
  isAllApproved: boolean;
  /** Toggle auto-approval for all non-DELETE changes on or off. */
  setIsAllApproved: (value: boolean) => void;
  /** Returns `true` when every resource path in the set has been pre-approved. */
  areResourcesApproved: (paths: Set<string>) => boolean;
  /** Add resource paths to the pre-approved set. */
  approveResources: (paths: Iterable<string>) => void;
};

const ApprovedResourcesContext = createContext<ApprovedResourcesContextValue | null>(null);

export function ApprovedResourcesProvider({ children }: { children: ReactNode }) {
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [approvedPaths, setApprovedPaths] = useState<Set<string>>(() => new Set());

  const approveResources = useCallback((paths: Iterable<string>) => {
    setApprovedPaths((previous) => {
      const next = new Set(previous);
      let changed = false;
      for (const p of paths) {
        if (!next.has(p)) {
          next.add(p);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, []);

  const areResourcesApproved = useCallback(
    (paths: Set<string>) => {
      if (paths.size === 0) return true;
      for (const p of paths) {
        if (!approvedPaths.has(p)) return false;
      }
      return true;
    },
    [approvedPaths],
  );

  return (
    <ApprovedResourcesContext.Provider
      value={{
        isAllApproved,
        setIsAllApproved,
        areResourcesApproved,
        approveResources,
      }}
    >
      {children}
    </ApprovedResourcesContext.Provider>
  );
}

export function useApprovedResources(): ApprovedResourcesContextValue {
  const context = use(ApprovedResourcesContext);
  if (context == null) {
    throw new Error('useApprovedResources must be used within an ApprovedResourcesProvider');
  }
  return context;
}
